import {DrawerContext} from '@yandex-delivery/blocks/legacy/drawer/context'
import clsx from 'clsx'
import React, {useContext, useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import type {ContentActionButtonItem, ContentSection, ContentSpacerItem} from 'src/api/types'

import {ActionButton} from './ActionButton'
import {classes} from './BottomContentSections.styles'
import {Spacer} from './Spacer'

type Props = {
  sections: ContentSection[]
  /** Мобилка: закрепить бар внизу экрана (floaty), контент скроллится под ним. */
  floating?: boolean
}

/**
 * Блок bottom_content_sections (кнопка оплаты «внизу», pay-for-order).
 * Рендерит item'ы (spacer / action_button) напрямую, без DrawerSection-обёртки
 * (у неё min-height 56px, который ломает spacer'ы).
 *
 * В floaty-режиме бар портируется в контейнер DrawerOverlay и фиксируется внизу
 * экрана, а на его месте в потоке остаётся плейсхолдер той же высоты — чтобы при
 * полном скролле бар не перекрывал контент (напр. кнопку «Помощь»).
 *
 * Именно в контейнер DrawerOverlay, а не в body: шторка и модалки живут в одном
 * контейнере и перекрывают друг друга порядком DOM, z-index'ом между них не
 * встать. Бар монтируется после шторки и до модалок, поэтому он поверх шторки,
 * а попап статусов — поверх него (CARGODEV-85875).
 */
export const BottomContentSections: React.FC<Props> = ({sections, floating = false}) => {
  const [mounted, setMounted] = useState(false)
  const barRef = useRef<HTMLDivElement>(null)
  const [barHeight, setBarHeight] = useState(0)
  const {containerRef} = useContext(DrawerContext)

  useEffect(() => setMounted(true), [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: sections в списке намеренно — высоту бара пересчитываем на смену контента
  useEffect(() => {
    if (floating && mounted && barRef.current) {
      setBarHeight(barRef.current.offsetHeight)
    }
  }, [floating, mounted, sections])

  const items = sections.flatMap(section => section.items ?? []) as unknown as Array<{type: string}>

  if (items.length === 0) {
    return null
  }

  const bar = (
    <div className={clsx(classes.root, floating && classes.floating)} data-e2e="floating-pay-bar" ref={barRef}>
      {items.map((item, index) => {
        if (item.type === 'action_button') {
          return <ActionButton item={item as ContentActionButtonItem} key={index} />
        }
        if (item.type === 'spacer') {
          return <Spacer item={item as ContentSpacerItem} key={index} />
        }
        return null
      })}
    </div>
  )

  if (floating && mounted && containerRef?.current) {
    return (
      <>
        {createPortal(bar, containerRef.current)}
        {/* Резерв места в потоке под fixed-бар, чтобы он не перекрывал контент.
            flex-shrink: 0 — иначе схлопывается во flex-колонке шторки. */}
        <div aria-hidden style={{blockSize: barHeight, minBlockSize: barHeight, flexShrink: 0}} />
      </>
    )
  }

  return bar
}
